import { useMemo } from "react";
import type { DatasourceMutationRequest } from "pages/grid/components";
import {
  useGetAdminTutorialsQuery,
  useGetAdminUsersQuery,
  useGetDataSourcesQuery,
  useGetEnvironnementsQuery,
  useGetFAQQuery,
  useGetLayoutDefQuery,
  useGetMenuQuery,
  useGetTutorialsQuery,
  useGetVersionsQuery,
  useReadDatasourceQuery,
} from "store/api";
import { getEnvCode } from "utils/hooks/useSalesNetworks";
import {
  buildMutationRequest,
  joinBaseUrlAndPath,
  normalizeBareNumericEnvPath,
  replaceParams,
  replaceTrailingNumericWithEnvLang,
  resolveEnvNumericId,
  usesDecodeParamsEnvSegment,
} from "pages/grid/utils/gridHelpers";

export type TableRecord = Record<string, any>;

const TUTORIALS_LAYOUT_ID = 23;
const ADMIN_TUTORIALS_LAYOUT_ID = 25;

export function useGridDataSourceFlow(layoutId?: string) {
  const isDashboardLayout = layoutId != null && Number(layoutId) === 1;

  const {
    data: layoutDef,
    isLoading: layoutLoading,
    isFetching: layoutFetching,
  } = useGetLayoutDefQuery(layoutId || "", {
    skip: !layoutId,
    refetchOnMountOrArgChange: true,
  });

  const layoutDefForPage =
    layoutDef == null
      ? null
      : (layoutDef as { id?: number }).id != null &&
        Number((layoutDef as { id?: number }).id) !== Number(layoutId)
      ? null
      : layoutDef;

  const { data: dataSources = [], isLoading: dataSourcesLoading } = useGetDataSourcesQuery("");
  const { data: environnements = [], refetch: refetchEnvironments } = useGetEnvironnementsQuery("");

  const envLang = useMemo(() => {
    const storedEnvId = localStorage.getItem("_env_id");
    const storedEnvCode = localStorage.getItem("_env") || getEnvCode() || "";
    const lang = localStorage.getItem("_lang") || "fr";
    const envNumeric = resolveEnvNumericId(environnements, storedEnvId, storedEnvCode);
    return { lang, envNumeric };
  }, [environnements]);

  const {
    data: menuRows,
    isLoading: menuTableLoading,
    isFetching: menuTableFetching,
    refetch: refetchMenu,
  } = useGetMenuQuery("", {
    skip: !isDashboardLayout,
  });

  const layoutItems = useMemo(() => {
    const source = (layoutDefForPage as { layout?: any[] } | null)?.layout;
    return Array.isArray(source) ? source : [];
  }, [layoutDefForPage]);

  const tableWidget = useMemo(() => {
    const found = layoutItems.find((item: any) => item?.widget?.type === "TableWidget");
    return found?.widget || null;
  }, [layoutItems]);

  const selectedDataSource = useMemo(() => {
    if (!tableWidget?.table) return null;
    return dataSources.find((d: any) => Number(d.id) === Number(tableWidget.table)) || null;
  }, [dataSources, tableWidget]);

  const isEnvironmentTable = useMemo(() => {
    const source = [tableWidget?.title, tableWidget?.name, selectedDataSource?.name, selectedDataSource?.baseUrl]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return /environ/.test(source);
  }, [tableWidget, selectedDataSource]);

  const isAdminUsersTable = useMemo(() => {
    const fieldNames = (tableWidget?.fields ?? []).map((f: any) => String(f?.field ?? "").toLowerCase());
    const source = [
      tableWidget?.title,
      tableWidget?.name,
      selectedDataSource?.name,
      selectedDataSource?.baseUrl,
      selectedDataSource?.read?.endpoint,
      ...fieldNames,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const hasAdminUserEndpoint = /(^|[^a-z])adminuser([^a-z]|$)/.test(source);
    const hasAdminUsersColumns =
      fieldNames.includes("username") &&
      fieldNames.includes("email") &&
      fieldNames.includes("entities") &&
      fieldNames.includes("environnement_mgt");

    return hasAdminUserEndpoint || hasAdminUsersColumns;
  }, [tableWidget, selectedDataSource]);

  const isTutorialsLayout = useMemo(() => {
    if (isDashboardLayout) return false;
    if (Number(layoutId) === TUTORIALS_LAYOUT_ID) return true;
    const names = (tableWidget?.fields ?? []).map((f: any) => f.field);
    return names.includes("videoName") && names.includes("description");
  }, [isDashboardLayout, layoutId, tableWidget]);

  const isAdminTutorialsLayout = useMemo(() => {
    if (!isTutorialsLayout) return false;
    if (Number(layoutId) === ADMIN_TUTORIALS_LAYOUT_ID) return true;

    const source = [tableWidget?.title, tableWidget?.name, layoutDefForPage?.title, layoutDefForPage?.name]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return /admin.*tutor|tutor.*admin/.test(source);
  }, [isTutorialsLayout, layoutId, tableWidget, layoutDefForPage]);

  const {
    data: tutorialsRowsData,
    isLoading: tutorialsLoadingBase,
    isFetching: tutorialsFetchingBase,
    refetch: refetchTutorialsBase,
  } = useGetTutorialsQuery(
    {
      environnement_id: envLang.envNumeric ?? 0,
      lang: envLang.lang,
    },
    {
      skip:
        !isTutorialsLayout ||
        isAdminTutorialsLayout ||
        envLang.envNumeric == null ||
        Number.isNaN(envLang.envNumeric),
    }
  );

  const {
    data: adminTutorialsRowsData,
    isLoading: adminTutorialsLoading,
    isFetching: adminTutorialsFetching,
    refetch: refetchAdminTutorials,
  } = useGetAdminTutorialsQuery(
    {
      environnement_id: envLang.envNumeric ?? 0,
      lang: envLang.lang,
    },
    {
      skip:
        !isAdminTutorialsLayout ||
        envLang.envNumeric == null ||
        Number.isNaN(envLang.envNumeric),
    }
  );

  const tutorialsRows = isAdminTutorialsLayout ? adminTutorialsRowsData : tutorialsRowsData;
  const tutorialsLoading = isAdminTutorialsLayout ? adminTutorialsLoading : tutorialsLoadingBase;
  const tutorialsFetching = isAdminTutorialsLayout ? adminTutorialsFetching : tutorialsFetchingBase;
  const refetchTutorials = isAdminTutorialsLayout ? refetchAdminTutorials : refetchTutorialsBase;

  const { data: adminUsersRows = [], refetch: refetchAdminUsers } = useGetAdminUsersQuery("table-read", {
    skip: !isAdminUsersTable || isDashboardLayout || isTutorialsLayout,
    refetchOnMountOrArgChange: true,
  });

  const isFaqTable = useMemo(() => {
    const fieldNames = (tableWidget?.fields ?? []).map((f: any) => String(f?.field ?? "").toLowerCase());
    const source = [
      tableWidget?.title,
      tableWidget?.name,
      selectedDataSource?.name,
      selectedDataSource?.baseUrl,
      selectedDataSource?.read?.endpoint,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const hasNameMatch = /(^|[^a-z])faq([^a-z]|$)/.test(source);
    const hasColumnsMatch =
      fieldNames.includes("question_fr") &&
      fieldNames.includes("question_en") &&
      fieldNames.includes("answer_fr") &&
      fieldNames.includes("answer_en");
    return hasNameMatch || hasColumnsMatch;
  }, [tableWidget, selectedDataSource]);

  const isVersionsTable = useMemo(() => {
    const fieldNames = (tableWidget?.fields ?? []).map((f: any) => String(f?.field ?? "").toLowerCase());
    const source = [
      tableWidget?.title,
      tableWidget?.name,
      layoutDefForPage?.title,
      layoutDefForPage?.name,
      selectedDataSource?.name,
      selectedDataSource?.baseUrl,
      selectedDataSource?.read?.endpoint,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const hasNameMatch =
      /(^|[^a-z])versions?([^a-z]|$)|[eé]volution\s*pjp4|gestion\s*[eé]volution/.test(source);
    const hasColumnsMatch =
      fieldNames.includes("version") &&
      fieldNames.includes("fr") &&
      fieldNames.includes("en");

    return hasNameMatch || hasColumnsMatch;
  }, [tableWidget, layoutDefForPage, selectedDataSource]);

  const {
    data: versionsRows,
    isLoading: versionsLoading,
    isFetching: versionsFetching,
    refetch: refetchVersions,
  } = useGetVersionsQuery(null, {
    skip: !isVersionsTable || isDashboardLayout,
    refetchOnMountOrArgChange: true,
  });

  const { data: faqRows, isLoading: faqLoading, isFetching: faqFetching, refetch: refetchFaq } = useGetFAQQuery(null, {
    skip: !isFaqTable || isDashboardLayout || isVersionsTable,
    refetchOnMountOrArgChange: true,
  });

  const datasourceRequest = useMemo(() => {
    if (isDashboardLayout || isTutorialsLayout) return null;
    if (!selectedDataSource?.read?.endpoint) return null;
    const { lang, envNumeric } = envLang;
    if (envNumeric == null || Number.isNaN(envNumeric)) return null;

    const rawEndpoint = selectedDataSource.read.endpoint as string;
    const baseUrl = selectedDataSource.baseUrl || "";
    const useComposite =
      usesDecodeParamsEnvSegment(rawEndpoint) ||
      usesDecodeParamsEnvSegment(baseUrl) ||
      /##env##/.test(rawEndpoint);
    const envComposite = `env=${envNumeric}&lang=${lang}`;
    const params: Record<string, string | number> = {
      env: useComposite ? envComposite : String(envNumeric),
      lang,
    };

    let endpoint = replaceParams(rawEndpoint, params);
    const hasEnvPlaceholder = /\{env\}|:env\b|##env##/.test(rawEndpoint);
    if (!hasEnvPlaceholder && /\/$/.test(endpoint)) {
      endpoint = `${endpoint}${useComposite ? envComposite : String(envNumeric)}`;
    }
    endpoint = replaceTrailingNumericWithEnvLang(baseUrl, endpoint, envNumeric, lang);
    endpoint = normalizeBareNumericEnvPath(endpoint, envNumeric, lang);
    endpoint = endpoint.replace(/\/+$/, "");
    const finalUrl = joinBaseUrlAndPath(baseUrl, endpoint);

    if (import.meta.env.DEV) {
      console.debug("[GridPage datasource]", {
        layoutId,
        rawEndpoint: selectedDataSource.read.endpoint,
        baseUrl: selectedDataSource.baseUrl,
        builtEndpoint: endpoint,
        finalUrl,
        useComposite,
        envNumeric,
        lang,
      });
    }

    return {
      url: finalUrl,
      method: selectedDataSource.read.method || "GET",
    };
  }, [envLang, selectedDataSource, isDashboardLayout, isTutorialsLayout, layoutId]);

  const readDatasourceArgs = useMemo(() => {
    if (!datasourceRequest || layoutId == null || layoutId === "") return null;
    const dsId = selectedDataSource?.id != null ? Number(selectedDataSource.id) : undefined;
    return {
      ...datasourceRequest,
      layoutId,
      ...(dsId != null && !Number.isNaN(dsId) ? { datasourceId: dsId } : {}),
    };
  }, [datasourceRequest, layoutId, selectedDataSource?.id]);

  const {
    data: datasourceResponse,
    isLoading: tableLoading,
    isFetching: tableFetching,
    refetch: refetchDatasource,
  } = useReadDatasourceQuery(readDatasourceArgs as any, {
    skip: !readDatasourceArgs || layoutDefForPage == null,
    refetchOnMountOrArgChange: true,
  });

  const rows: TableRecord[] = useMemo(() => {
    if (isDashboardLayout && menuRows) {
      const source = Array.isArray(menuRows) ? menuRows : [];
      return source.map((row: any, index: number) => ({
        key: row.id ?? index + 1,
        ...row,
      }));
    }
    if (isTutorialsLayout && tutorialsRows) {
      const source = Array.isArray(tutorialsRows) ? tutorialsRows : [];
      return source.map((row: any, index: number) => ({
        key: row.id ?? index + 1,
        ...row,
      }));
    }
    if (isEnvironmentTable) {
      const source = Array.isArray(environnements) ? environnements : [];
      return source.map((row: any, index: number) => ({
        key: row.id ?? index + 1,
        ...row,
      }));
    }
    if (isAdminUsersTable) {
      const source = Array.isArray(adminUsersRows) ? adminUsersRows : [];
      return source.map((row: any, index: number) => ({
        key: row.id ?? index + 1,
        ...row,
      }));
    }
    if (isVersionsTable) {
      const versionsData = Array.isArray(versionsRows)
        ? versionsRows
        : Array.isArray((versionsRows as any)?.data)
        ? (versionsRows as any).data
        : [];
      return versionsData.map((row: any, index: number) => ({
        key: row.id ?? index + 1,
        ...row,
      }));
    }
    if (isFaqTable) {
      const faqData = Array.isArray(faqRows) ? faqRows : Array.isArray((faqRows as any)?.data) ? (faqRows as any).data : [];
      return faqData.map((row: any, index: number) => ({
        key: row.id ?? index + 1,
        ...row,
      }));
    }
    if (!readDatasourceArgs) return [];
    const source = Array.isArray(datasourceResponse)
      ? datasourceResponse
      : Array.isArray((datasourceResponse as any)?.data)
      ? (datasourceResponse as any).data
      : [];
    return source.map((row: any, index: number) => ({
      key: row.id ?? index + 1,
      ...row,
    }));
  }, [
    isDashboardLayout,
    isTutorialsLayout,
    isEnvironmentTable,
    isAdminUsersTable,
    isFaqTable,
    isVersionsTable,
    menuRows,
    tutorialsRows,
    environnements,
    adminUsersRows,
    faqRows,
    versionsRows,
    datasourceResponse,
    readDatasourceArgs,
  ]);

  const tableTitle = useMemo(() => {
    const raw =
      tableWidget?.title ??
      tableWidget?.name ??
      layoutDefForPage?.title ??
      layoutDefForPage?.name ??
      "";
    return String(raw).trim();
  }, [tableWidget, layoutDefForPage]);

  const layoutPageTitle = useMemo(() => {
    const raw = layoutDefForPage?.title ?? layoutDefForPage?.name ?? "";
    return String(raw).trim();
  }, [layoutDefForPage]);

  const uploadEndpoint = useMemo(() => {
    switch (tableTitle) {
      case "Translations":
        return "/translation";
      case "Pompes":
        return "/heatpumpMassEdit";
      case "Accessoires":
        return "/accessorymgtMassEdit";
      case "Discount Management":
        return "/admin/heatpumpDiscountsMassEdit";
      default:
        return null;
    }
  }, [tableTitle]);

  const createDatasourceRequest = useMemo<DatasourceMutationRequest | null>(() => {
    if (isDashboardLayout) return null;
    return buildMutationRequest(
      selectedDataSource?.create,
      "POST",
      selectedDataSource?.baseUrl || "",
      envLang
    );
  }, [envLang, selectedDataSource, isDashboardLayout]);

  const updateDatasourceRequest = useMemo<DatasourceMutationRequest | null>(() => {
    if (isDashboardLayout || (isTutorialsLayout && !isAdminTutorialsLayout)) return null;
    return buildMutationRequest(
      selectedDataSource?.update,
      "PATCH",
      selectedDataSource?.baseUrl || "",
      envLang
    );
  }, [envLang, selectedDataSource, isDashboardLayout, isTutorialsLayout, isAdminTutorialsLayout]);

  const trashDatasourceRequest = useMemo<DatasourceMutationRequest | null>(() => {
    if (isTutorialsLayout && !isAdminTutorialsLayout) return null;
    if (isDashboardLayout) {
      const baseUrl = String(selectedDataSource?.baseUrl ?? "").toLowerCase();
      const name = String(selectedDataSource?.name ?? "").toLowerCase();
      const isMenuDefsDatasource = baseUrl.includes("/menudefs") || /(^|[^a-z])menu([^a-z]|$)/.test(name);
      if (!isMenuDefsDatasource) return null;
    }
    if (!selectedDataSource?.trash?.active) return null;
    if (!selectedDataSource?.trash?.endpoint) return null;

    return {
      url: selectedDataSource.trash.endpoint,
      method: selectedDataSource.trash.method || "DELETE",
    };
  }, [selectedDataSource, isDashboardLayout, isTutorialsLayout, isAdminTutorialsLayout]);

  return {
    envLang,
    layoutItems,
    isDashboardLayout,
    isTutorialsLayout,
    isAdminTutorialsLayout,
    isFaqTable,
    isVersionsTable,
    faqLoading,
    faqFetching,
    refetchFaq,
    refetchAdminUsers,
    refetchEnvironments,
    versionsLoading,
    versionsFetching,
    refetchVersions,
    tableWidget,
    tableTitle,
    layoutPageTitle,
    selectedDataSource,
    rows,
    uploadEndpoint,
    createDatasourceRequest,
    updateDatasourceRequest,
    trashDatasourceRequest,
    refetchDatasource,
    layoutLoading,
    layoutFetching,
    dataSourcesLoading,
    menuTableLoading,
    menuTableFetching,
    refetchMenu,
    tutorialsLoading,
    tutorialsFetching,
    refetchTutorials,
    tableLoading,
    tableFetching,
  };
}
